import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "./cityPricing";

const grasse: ChauffeurPage = {
  slug: "grasse",
  title: "Chauffeur privé à Grasse – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Grasse : transferts aéroport de Nice, parfumeries Fragonard, Molinard et Galimard, vieille ville et arrière-pays, 7j/7.",
  keywords: [
    "chauffeur privé grasse",
    "vtc grasse",
    "chauffeur grasse",
    "transfert grasse aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Grasse",
    subtitle:
      "Transferts aéroport de Nice, parfumeries, vieille ville et arrière-pays — disponible 24h/24, 7j/7.",
    keywords: [
      "VTC Grasse",
      "chauffeur parfumeries",
      "transfert aéroport Nice",
      "capitale du parfum",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Grasse",
    badges: ["Disponible 24h/24", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Grasse",
    items: [
      {
        title: "Aéroport de Nice en direct",
        description:
          "Aéroport de Nice à environ 50 min par l’A8, suivi de vol et accueil personnalisé.",
      },
      {
        title: "Parfumeries & visites",
        description:
          "Fragonard, Molinard et Galimard : dépose devant les maisons de parfum, attente pendant la visite et l’atelier.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, ponctuels et discrets, à l’aise dans la vieille ville et les routes de l’arrière-pays.",
      },
      {
        title: "Ancrage local",
        description: "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Grasse",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge avec suivi de vol depuis et vers l’aéroport de Nice Côte d’Azur et les gares.",
      },
      {
        title: "Tour des parfumeries",
        text: "Fragonard, Molinard, Galimard et le Musée International de la Parfumerie, avec attente pendant les visites.",
      },
      {
        title: "Vieille ville & culture",
        text: "Cathédrale Notre-Dame-du-Puy, Villa-Musée Fragonard et ruelles historiques, dépose au plus près.",
      },
      {
        title: "Arrière-pays & champs de fleurs",
        text: "Champs de jasmin et de rose de mai, Gourdon et les Gorges du Loup, au rythme de la lumière.",
      },
      {
        title: "Mise à disposition & longue distance",
        text: "À l’heure, à la journée ou toute distance : Cannes, Nice, Monaco et Italie.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Parfumerie Fragonard",
        description: "Maison de parfum historique et usine, au cœur de Grasse.",
      },
      {
        name: "Molinard & Galimard",
        description: "Maisons de parfum et ateliers de création de fragrances.",
      },
      {
        name: "Musée International de la Parfumerie",
        description: "Musée dédié à l’histoire du parfum, en vieille ville.",
      },
      {
        name: "Vieille ville & cathédrale",
        description: "Notre-Dame-du-Puy, ruelles et places historiques.",
      },
      {
        name: "Villa-Musée Fragonard",
        description: "Demeure et musée du peintre Jean-Honoré Fragonard.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 50 minutes par l’autoroute A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour tout Grasse",
    subheading:
      "De la capitale du parfum à l’arrière-pays, un service premium sur les routes des collines.",
    paragraphs: [
      "KS Rent Services assure vos déplacements avec chauffeur privé à Grasse et sur toute la Côte d’Azur : transferts depuis l’aéroport de Nice (environ 50 minutes), tour des parfumeries et trajets longue distance.",
      "Capitale mondiale du parfum, Grasse s’étage dans les collines au-dessus de Cannes : nos chauffeurs bilingues connaissent Fragonard, Molinard et Galimard, la vieille ville et les routes de l’arrière-pays.",
      "Entre une visite d’usine, un atelier de création de parfum et une escapade vers Gourdon et les Gorges du Loup, votre chauffeur vous dépose devant chaque maison et vous attend, sans souci de stationnement.",
    ],
    bullets: [
      "Suivi de vol & accueil personnalisé",
      "Attente pendant les visites d’usine",
      "Routes de l’arrière-pays maîtrisées",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Grasse",
    steps: [
      "Indiquez votre trajet, vos horaires et le nombre de passagers.",
      "Nous confirmons le véhicule adapté et le forfait.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Ajustements en direct selon le trafic et vos imprévus.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Grasse"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Grasse ?",
      a: "Comptez environ 50 minutes par l’autoroute A8 dans des conditions normales. Nous assurons les transferts 24h/24 avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Emmenez-vous les visiteurs dans les parfumeries de Grasse ?",
      a: "Oui, Fragonard, Molinard, Galimard et le Musée International de la Parfumerie, avec dépose devant chaque maison et attente pendant la visite et l’atelier.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à Grasse ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et pendant la Fête du Jasmin ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Proposez-vous des excursions dans l’arrière-pays grassois ?",
      a: "Oui, Gourdon, les Gorges du Loup et les champs de fleurs, avec un chauffeur à l’aise sur les routes de montagne.",
    },
  ],
  relatedLinks: [
    { label: "VTC à Cannes", href: "/chauffeur/cannes" },
    {
      label: "Navette aéroport de Nice",
      href: "/chauffeur/aeroport",
    },
    {
      label: "VTC Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Location de véhicule à Grasse",
      href: "/location/location-voiture-grasse",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Grasse",
    text: "Réservez votre transfert ou votre mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Grasse 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Grasse : transfert aéroport de Nice (~50 min), parfumeries Fragonard, Molinard et Galimard, vieille ville et arrière-pays.",
    canonical: "/chauffeur/grasse",
  },
};

export default grasse;
